/*
 * Detects whether a WorkSpace should open the agentic client instead of the
 * standard streaming client.
 *
 * The signal is a marker inside GetResources'
 * `Resources[0].ResourceDetails.WorkspaceCapabilities`, which is a stringified
 * JSON array of capability strings. The field placement and the marker value
 * below are a proposed convention and may change once the capability contract
 * is finalized.
 */

// Marker string expected inside the WorkspaceCapabilities array. Proposed value.
export const AGENTIC_CAPABILITY = 'WS4B_AGENTIC';

// Dev-only override: forces agentic mode without a real capability. Persisted in
// localStorage so it survives the sign-in redirect and the hard navigations back
// to the client. Never consulted in a production build (see isForceAgenticActive).
const FORCE_AGENTIC_STORAGE_KEY = 'ws4b_force_agentic';
const FORCE_AGENTIC_QUERY_PARAM = 'forceAgentic';

const isDevBuild = (): boolean => process.env.NODE_ENV !== 'production';

/**
 * Persists the `?forceAgentic=1` query flag into localStorage on first sight so
 * it outlives the multiple full-page navigations of the sign-in flow. No-op in a
 * production build and when the flag is absent. Safe to call repeatedly.
 */
export const captureForceAgenticFlag = (): void => {
  if (!isDevBuild()) {
    return;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(FORCE_AGENTIC_QUERY_PARAM) === '1') {
      window.localStorage.setItem(FORCE_AGENTIC_STORAGE_KEY, '1');
    }
  } catch {
    // window/localStorage unavailable — nothing to capture.
  }
};

const isForceAgenticActive = (): boolean => {
  if (!isDevBuild()) {
    return false;
  }
  captureForceAgenticFlag();
  const envFlag = process.env.REACT_APP_FORCE_AGENTIC;
  if (envFlag === '1' || envFlag === 'true') {
    return true;
  }
  try {
    return window.localStorage.getItem(FORCE_AGENTIC_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const parseWorkspaceCapabilities = (raw?: string | null): string[] => {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return [];
  }
};

/**
 * Returns true when the WorkSpace advertises agentic capability, or when the
 * dev-only override is active (non-production builds only).
 *
 * @param workspaceCapabilities the raw stringified capability array from
 *   `Resources[0].ResourceDetails.WorkspaceCapabilities`
 */
export const isAgenticWorkspace = (
  workspaceCapabilities?: string | null
): boolean => {
  if (isForceAgenticActive()) {
    return true;
  }
  return parseWorkspaceCapabilities(workspaceCapabilities).includes(
    AGENTIC_CAPABILITY
  );
};
